import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { downloadReviewDossier } from '../utils/reviewDossier';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Collapse,
    Badge,
    Stat,
    StatLabel,
    StatNumber,
    IconButton,
    Tooltip,
    Icon,
    Divider,
} from '@chakra-ui/react';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, DownloadIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { Tooltip as RechartsTooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import UploadYourFile from './UploadYourFile';
import Loading from '../compnents/Loading';
import { FaLock, FaSearch, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import EmptyStateCard from '../compnents/EmptyStateCard';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function ReportsExports() {
    const { token, user, setUser, isAuthInitializing } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [pagination, setPagination] = useState({ total_count: 0, page: 1, page_size: 25 });
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const [customerReports, setCustomerReports] = useState({});
    const [loadingReports, setLoadingReports] = useState({});
    const [downloadingPDF, setDownloadingPDF] = useState({});
    const [exportingCSV, setExportingCSV] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    // Fetch customers list
    const fetchCustomers = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const params = { page, page_size: 25 };
            if (search) params.search = search;

            const res = await axios.get(`${API_BASE_URL}/content/list-customers`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setCustomers(res.data.customers);
            setPagination(res.data.pagination);
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load customers.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    // Initial load customers
    useEffect(() => {
        if (user && user.noOfFileUploads > 0 && ['solo', 'pro', 'business'].includes(user.subscription) && user.role === 'admin') {
            fetchCustomers();
        }
        // eslint-disable-next-line
    }, [user]);

    // Fetch customer bias report
    const fetchCustomerBiasReport = async (customerId) => {
        if (customerReports[customerId]) return; // Already loaded

        try {
            setLoadingReports(prev => ({ ...prev, [customerId]: true }));
            const res = await axios.get(`${API_BASE_URL}/content/admin-cutomer-bias-report/${customerId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setCustomerReports(prev => ({ ...prev, [customerId]: res.data }));
        } catch (error) {
            toast({
                title: 'Error',
                description: `Failed to load bias report for customer ${customerId}.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoadingReports(prev => ({ ...prev, [customerId]: false }));
        }
    };

    // Toggle customer report view
    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
            fetchCustomerBiasReport(customerId);
        }
    };

    // Handle search
    const handleSearch = () => {
        fetchCustomers(1, searchTerm);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        fetchCustomers(1, '');
    };

    const [downloadingDossier, setDownloadingDossier] = useState({});
    const downloadDossier = async (customerId) => {
        setDownloadingDossier(prev => ({ ...prev, [customerId]: true }));
        try {
            await downloadReviewDossier(customerId, token);
        } catch (error) {
            toast({ title: 'Dossier unavailable', description: `The review dossier for ${customerId} could not be downloaded.`, status: 'error', duration: 5000, isClosable: true, position: 'top' });
        } finally {
            setDownloadingDossier(prev => ({ ...prev, [customerId]: false }));
        }
    };

    // Download customer PDF report
    const downloadCustomerPDFReport = async (customerId) => {
        try {
            setDownloadingPDF(prev => ({ ...prev, [customerId]: true }));
            const res = await axios.get(`${API_BASE_URL}/content/admin-export-customer-report/${customerId}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `customer_${customerId}_bias_report.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: 'Success',
                description: `Customer report downloaded successfully.`,
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: `Failed to download report for customer ${customerId}.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setDownloadingPDF(prev => ({ ...prev, [customerId]: false }));
        }
    };

    // Export all customers CSV
    const exportAllCustomersCSV = async () => {
        try {
            setExportingCSV(true);
            const res = await axios.get(`${API_BASE_URL}/content/admin-cum-rm-export-customers`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'all-customers.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: 'Success',
                description: 'All customers exported successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to export customers CSV.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setExportingCSV(false);
        }
    };

    // Export my customers CSV (for RM)
    const exportMyCustomersCSV = async () => {
        try {
            setExportingCSV(true);
            const res = await axios.get(`${API_BASE_URL}/content/admin-cum-rm-export-customers?rm_only=true`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'my-customers.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: 'Success',
                description: 'Your customers exported successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to export your customers CSV.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setExportingCSV(false);
        }
    };

    // Export all bias scores CSV
    const exportBiasScoresCSV = async () => {
        try {
            setExportingCSV(true);
            const res = await axios.get(`${API_BASE_URL}/content/admin-export-bias-scores`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'bias_scores.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: 'Success',
                description: 'Bias scores exported successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to export bias scores CSV.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setExportingCSV(false);
        }
    };

    // Get color for bias score
    const getScoreColor = (score) => {
        if (score >= 80) return '#8B0000';
        if (score >= 70) return '#DC143C';
        if (score >= 60) return '#FF6347';
        if (score >= 50) return '#FF8C00';
        if (score >= 40) return '#FFA500';
        if (score >= 30) return '#FFD700';
        if (score >= 20) return '#FFFFE0';
        if (score >= 10) return '#90EE90';
        return '#228B22';
    };

    // Get risk alignment color
    const getRiskAlignmentColor = (status) => {
        if (status === 'high' || status === 'good') return 'green';
        if (status === 'medium') return 'yellow';
        return 'red';
    };

    // Render radar chart
    const renderRadarChart = (radar) => {
        const radarData = Object.entries(radar).map(([key, value]) => ({
            category: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            score: value,
        }));

        return (
            <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                    <PolarGrid stroke="#E2E8F0" />
                    <PolarAngleAxis dataKey="category" stroke="#4A5568" tick={{ fontSize: 10 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="#4A5568" tick={false} axisLine={false} />
                    <Radar name="Score" dataKey="score" stroke="#0B2545" fill="#0B2545" fillOpacity={0.6} />
                    <RechartsTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                </RadarChart>
            </ResponsiveContainer>
        );
    };

    // Render bias heatmap
    const renderBiasHeatmap = (biases) => {
        const biasEntries = Object.entries(biases);
        const cols = 5;
        const rows = Math.ceil(biasEntries.length / cols);

        return (
            <Box bg="white" p={4} borderRadius="md" border="1px solid" borderColor="gray.300" overflow="auto">
                <Flex justify="flex-end" mb={4} align="center">
                    <Text fontSize="xs" fontWeight="bold" mr={2} color="gray.600">Score:</Text>
                    <HStack spacing={0}>
                        {[0, 20, 40, 60, 80, 100].map((val) => (
                            <Box key={val} textAlign="center">
                                <Box w="30px" h="15px" bg={getScoreColor(val)} border="1px solid" borderColor="gray.300" />
                                <Text fontSize="9px" color="gray.600">{val}</Text>
                            </Box>
                        ))}
                    </HStack>
                </Flex>

                <Box display="inline-block" border="1px solid" borderColor="gray.400">
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <Flex key={rowIndex}>
                            {Array.from({ length: cols }).map((_, colIndex) => {
                                const index = rowIndex * cols + colIndex;
                                if (index >= biasEntries.length) {
                                    return <Box key={colIndex} w="140px" h="80px" bg="gray.50" borderRight="1px solid" borderBottom="1px solid" borderColor="gray.300" />;
                                }

                                const [key, value] = biasEntries[index];
                                const biasName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                                return (
                                    <Tooltip
                                        key={colIndex}
                                        label={
                                            <Box>
                                                <Text fontWeight="bold">{biasName}</Text>
                                                <Text>Score: {value.score}</Text>
                                                <Text>Confidence: {(value.confidence * 100).toFixed(0)}%</Text>
                                            </Box>
                                        }
                                        placement="top"
                                        hasArrow
                                    >
                                        <Box
                                            w="140px"
                                            h="80px"
                                            bg={getScoreColor(value.score)}
                                            borderRight="1px solid"
                                            borderBottom="1px solid"
                                            borderColor="gray.400"
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            cursor="pointer"
                                            transition="all 0.2s"
                                            position="relative"
                                            _hover={{ transform: 'scale(1.05)', zIndex: 10, boxShadow: '0 0 10px rgba(0,0,0,0.3)' }}
                                        >
                                            <Text fontSize="xs" fontWeight="bold" color={value.score > 50 ? "white" : "gray.800"} textAlign="center" px={2} noOfLines={2} lineHeight="1.2">
                                                {biasName}
                                            </Text>
                                            <Text fontSize="2xl" fontWeight="bold" color={value.score > 50 ? "white" : "gray.800"} mt={1}>
                                                {value.score}
                                            </Text>
                                            <Badge colorScheme={value.confidence >= 0.8 ? "green" : "yellow"} fontSize="8px" position="absolute" top={1} right={1}>
                                                {(value.confidence * 100).toFixed(0)}%
                                            </Badge>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Flex>
                    ))}
                </Box>
            </Box>
        );
    };

    // Render product recommendation
    const renderProductRecommendation = (productRec) => {
        if (!productRec) return null;

        const { recommended_product_type, product_code, bias_addresses, rationale, confidence, alternative_products, risk_alignment } = productRec;

        return (
            <Card bg="white" borderRadius="lg" boxShadow="md" borderWidth="2px" borderColor="brand.teal">
                <CardBody>
                    <VStack spacing={4} align="stretch">
                        <Flex justify="space-between" align="center">
                            <Box>
                                <Heading size="md" color="brand.navy">Product-Fit Signal</Heading>
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Decision support for the adviser review — not investment advice to the end client.
                                </Text>
                            </Box>
                            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>{(confidence * 100).toFixed(0)}% Fit</Badge>
                        </Flex>

                        <Box bg="#E8F4FF" p={4} borderRadius="md">
                            <HStack justify="space-between" mb={2}>
                                <Heading size="sm" color="brand.navy">{recommended_product_type}</Heading>
                                <Badge colorScheme="blue" fontSize="sm">{product_code}</Badge>
                            </HStack>
                            
                            {rationale && rationale.length > 0 && (
                                <VStack align="stretch" spacing={2} mt={3}>
                                    {rationale.map((item, index) => (
                                        <HStack key={index} align="start">
                                            <Icon as={CheckCircleIcon} color="green.500" mt={1} />
                                            <Text fontSize="sm" color="gray.700">{item}</Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            )}
                        </Box>

                        {risk_alignment && (
                            <Box bg="gray.50" p={4} borderRadius="md">
                                <Heading size="sm" mb={3} color="gray.700">Risk Alignment</Heading>
                                <SimpleGrid columns={2} spacing={4}>
                                    <Stat>
                                        <StatLabel fontSize="xs">Status</StatLabel>
                                        <StatNumber>
                                            <Badge colorScheme={getRiskAlignmentColor(risk_alignment.status)} fontSize="md">
                                                {risk_alignment.status.toUpperCase()}
                                            </Badge>
                                        </StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs">Product Risk</StatLabel>
                                        <StatNumber fontSize="lg">{risk_alignment.product_risk}/10</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs">Customer Tolerance</StatLabel>
                                        <StatNumber fontSize="lg">{risk_alignment.customer_tolerance}/10</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs">Harmony Score</StatLabel>
                                        <StatNumber fontSize="lg">{(risk_alignment.harmony * 100).toFixed(0)}%</StatNumber>
                                    </Stat>
                                </SimpleGrid>
                            </Box>
                        )}

                        {bias_addresses && Object.keys(bias_addresses).length > 0 && (
                            <Box>
                                <Heading size="sm" mb={3} color="gray.700">Biases Addressed</Heading>
                                <HStack spacing={3} flexWrap="wrap">
                                    {Object.entries(bias_addresses).map(([bias, score]) => (
                                        <Badge key={bias} colorScheme="purple" fontSize="sm" px={3} py={1}>
                                            {bias.replace(/_/g, ' ')}: {(score * 100).toFixed(0)}%
                                        </Badge>
                                    ))}
                                </HStack>
                            </Box>
                        )}

                        {alternative_products && alternative_products.length > 0 && (
                            <Box>
                                <Heading size="sm" mb={3} color="gray.700">Alternative Products</Heading>
                                <VStack spacing={2} align="stretch">
                                    {alternative_products.map((alt, index) => (
                                        <Box key={index} bg="gray.50" p={3} borderRadius="md">
                                            <HStack justify="space-between" mb={1}>
                                                <Text fontWeight="bold" fontSize="sm" color="gray.700">{alt.product_type}</Text>
                                                <Badge colorScheme="blue" fontSize="xs">Score: {(alt.score * 100).toFixed(0)}%</Badge>
                                            </HStack>
                                            <Text fontSize="xs" color="gray.600">{alt.product_code}</Text>
                                            <Text fontSize="xs" color="gray.600" mt={1}>{alt.rationale}</Text>
                                        </Box>
                                    ))}
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                </CardBody>
            </Card>
        );
    };

    // Render customer bias report
    const renderCustomerBiasReport = (customerId) => {
        if (loadingReports[customerId]) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Spinner size="xl" color="brand.navy" />
                </Flex>
            );
        }

        const reportData = customerReports[customerId];
        if (!reportData) {
            return <Text color="gray.500">No report data available.</Text>;
        }

        const { biases, radar, rationale, product_recommendation, scored_at } = reportData;

        return (
            <VStack spacing={6} align="stretch">
                {/* Product Recommendation */}
                {product_recommendation && renderProductRecommendation(product_recommendation)}

                {/* Radar Chart */}
                {radar && (
                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <Heading size="md" mb={4} color="brand.navy">Bias Category Scores</Heading>
                            {renderRadarChart(radar)}
                        </CardBody>
                    </Card>
                )}

                {/* Bias Heatmap */}
                {biases && (
                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <Heading size="md" mb={4} color="brand.navy">Behavioral Bias Heatmap</Heading>
                            <Text fontSize="sm" color="gray.600" mb={4}>
                                Color intensity indicates bias severity. Hover over cells for detailed information.
                            </Text>
                            {renderBiasHeatmap(biases)}
                        </CardBody>
                    </Card>
                )}

                {/* Rationale */}
                {rationale && rationale.bullets && rationale.bullets.length > 0 && (
                    <Card bg="#DCEEFF" borderRadius="lg" boxShadow="sm">
                        <CardBody>
                            <Heading size="sm" mb={3} color="brand.navy">Key Insights</Heading>
                            <VStack align="stretch" spacing={2}>
                                {rationale.bullets.map((item, index) => (
                                    <HStack key={index}>
                                        <Badge colorScheme="blue" fontSize="xs">{index + 1}</Badge>
                                        <Text fontSize="sm" color="gray.700">{item}</Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </CardBody>
                    </Card>
                )}

                {/* Metadata */}
                <Card bg="gray.50" borderRadius="lg" boxShadow="sm">
                    <CardBody>
                        <Text fontSize="xs" color="gray.500">
                            Report generated at: {new Date(scored_at).toLocaleString()}
                        </Text>
                    </CardBody>
                </Card>
            </VStack>
        );
    };

    // Loading state
    if (isAuthInitializing || !token) {
        return <Loading />;
    }

    // User not loaded
    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="brand.teal" />
            </Box>
        );
    }

    // No file uploads yet
    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                <Navbar />
                <UploadYourFile setUser={setUser} token={token} />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column">
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />
                <Container maxW="container.xl" flex="1" py={6}>
                    <VStack spacing={6} align="stretch">
                        {/* Free tier lock */}
                        {user.subscription === 'free' && (
                            <Box position="relative" mt={10}>
                                <Flex
                                    position="relative"
                                    minH="500px"
                                    zIndex={5}
                                    bg="rgba(255, 255, 255, 0.92)"
                                    backdropFilter="blur(3px)"
                                    borderRadius="xl"
                                    justify="center"
                                    align="center"
                                    flexDirection="column"
                                    textAlign="center"
                                    border="3px dashed"
                                    borderColor="teal.200"
                                    p={8}
                                >
                                    <Icon as={FaLock} boxSize={16} color="brand.navy" mb={4} />
                                    <Heading size="xl" mb={3} color="brand.navy">Premium Reports & Exports</Heading>
                                    <Text color="gray.700" maxW="md" mb={6} fontSize="lg" fontWeight="medium">
                                        Unlock comprehensive reports and data exports with FinanSee Premium. Export customer insights, behavioral analytics, and detailed reports in multiple formats.
                                    </Text>
                                    <Button
                                        size="lg"
                                        colorScheme="blue"
                                        bg="brand.navy"
                                        onClick={() => navigate('/subscription')}
                                        mb={3}
                                        px={8}
                                        py={6}
                                        fontSize="lg"
                                        _hover={{ bg: 'brand.navy2' }}
                                    >
                                        Upgrade to Premium
                                    </Button>
                                </Flex>
                            </Box>
                        )}

                        {/* Premium content - Admin view */}
                        {['solo', 'pro', 'business'].includes(user.subscription) && user.role === 'admin' && (
                            <>
                                {/* Header */}
                                <Box>
                                    <Heading size="lg" color="brand.navy" mb={2}>Reports & Exports</Heading>
                                    <Text color="gray.600" fontSize="sm">
                                        Download comprehensive reports and export customer data
                                    </Text>
                                </Box>

                                {/* Export Actions */}
                                <Card bg="white" borderRadius="lg" boxShadow="md">
                                    <CardBody>
                                        <Heading size="md" mb={4} color="brand.navy">Export Data</Heading>
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                            <Button
                                                leftIcon={<DownloadIcon />}
                                                colorScheme="blue"
                                                bg="brand.navy"
                                                onClick={exportAllCustomersCSV}
                                                isLoading={exportingCSV}
                                                _hover={{ bg: 'brand.navy2' }}
                                            >
                                                Export All Customers
                                            </Button>
                                            <Button
                                                leftIcon={<DownloadIcon />}
                                                colorScheme="purple"
                                                onClick={exportBiasScoresCSV}
                                                isLoading={exportingCSV}
                                            >
                                                Export Bias Scores
                                            </Button>
                                        </SimpleGrid>
                                    </CardBody>
                                </Card>

                                <Divider />

                                {/* Search Customers */}
                                <Card bg="white" borderRadius="lg" boxShadow="md">
                                    <CardBody>
                                        <Heading size="md" mb={4} color="brand.navy">Customer Reports</Heading>
                                        <HStack spacing={4}>
                                            <InputGroup flex={1}>
                                                <InputLeftElement pointerEvents="none">
                                                    <SearchIcon color="gray.400" />
                                                </InputLeftElement>
                                                <Input
                                                    placeholder="Search customers..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                                    borderColor="gray.300"
                                                    _focus={{ borderColor: '#0B2545', boxShadow: '0 0 0 1px #0B2545' }}
                                                />
                                            </InputGroup>
                                            <Button colorScheme="blue" bg="brand.navy" onClick={handleSearch} _hover={{ bg: 'brand.navy2' }}>
                                                Search
                                            </Button>
                                        </HStack>
                                    </CardBody>
                                </Card>

                                {/* Customer List */}
                                {loading ? (
                                    <Flex justify="center" align="center" py={20}>
                                        <Spinner size="xl" color="brand.navy" />
                                    </Flex>
                                ) : customers.length === 0 ? (
                                    <Card bg="white" borderRadius="lg" boxShadow="md">
                                        <CardBody>
                                            <EmptyStateCard
                                                icon={searchTerm ? FaSearch : FaUsers}
                                                title={searchTerm ? 'No customers match this search' : 'Customer reports will appear here once data is ready'}
                                                description={
                                                    searchTerm
                                                        ? `We could not find any customers matching "${searchTerm}".`
                                                        : 'Upload processing must complete before individual customer reports and exports can be generated.'
                                                }
                                                hint={
                                                    searchTerm
                                                        ? 'Try a customer ID, email, RM name, or clear the search to return to the full list.'
                                                        : 'If you uploaded a file recently, give the system a moment and then reload this page.'
                                                }
                                                borderColor="brand.divider"
                                                titleColor="gray.700"
                                                descriptionColor="gray.600"
                                                hintColor="gray.500"
                                                iconColor="#0B2545"
                                                maxW="full"
                                            >
                                                {searchTerm ? (
                                                    <Button
                                                        variant="outline"
                                                        borderColor="brand.teal"
                                                        color="brand.navy"
                                                        _hover={{ bg: 'brand.tealSoft' }}
                                                        onClick={handleClearSearch}
                                                    >
                                                        Clear search
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        colorScheme="blue"
                                                        bg="brand.navy"
                                                        _hover={{ bg: 'brand.navy2' }}
                                                        onClick={() => navigate('/datasets')}
                                                    >
                                                        View datasets
                                                    </Button>
                                                )}
                                            </EmptyStateCard>
                                        </CardBody>
                                    </Card>
                                ) : (
                                    <VStack spacing={4} align="stretch">
                                        {customers.map((customer) => (
                                            <Card
                                                key={customer.customer_id || customer.id}
                                                bg="white"
                                                borderRadius="lg"
                                                boxShadow="md"
                                                transition="all 0.2s"
                                                _hover={{ boxShadow: 'lg' }}
                                            >
                                                <CardBody>
                                                    <Flex justify="space-between" align="center">
                                                        <VStack align="start" spacing={1} flex={1}>
                                                            <Heading size="sm" color="brand.navy">
                                                                {customer.customer_id || customer.name || 'Unknown Customer'}
                                                            </Heading>
                                                            <HStack spacing={4}>
                                                                {customer.rm_name && (
                                                                    <Text fontSize="sm" color="gray.600">RM: {customer.rm_name}</Text>
                                                                )}
                                                                {customer.email && (
                                                                    <Text fontSize="sm" color="gray.600">{customer.email}</Text>
                                                                )}
                                                            </HStack>
                                                        </VStack>
                                                        <HStack spacing={2}>
                                                            <Button
                                                                size="sm"
                                                                leftIcon={<DownloadIcon />}
                                                                colorScheme="green"
                                                                onClick={() => downloadCustomerPDFReport(customer.customer_id || customer.id)}
                                                                isLoading={downloadingPDF[customer.customer_id || customer.id]}
                                                            >
                                                                PDF Report
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                leftIcon={<DownloadIcon />}
                                                                variant="outline"
                                                                colorScheme="teal"
                                                                onClick={() => downloadDossier(customer.customer_id || customer.id)}
                                                                isLoading={downloadingDossier[customer.customer_id || customer.id]}
                                                            >
                                                                Review dossier
                                                            </Button>
                                                            <IconButton
                                                                icon={
                                                                    expandedCustomer === (customer.customer_id || customer.id) ? (
                                                                        <ChevronUpIcon />
                                                                    ) : (
                                                                        <ChevronDownIcon />
                                                                    )
                                                                }
                                                                onClick={() => toggleCustomer(customer.customer_id || customer.id)}
                                                                colorScheme="blue"
                                                                variant="ghost"
                                                                aria-label="Toggle details"
                                                            />
                                                        </HStack>
                                                    </Flex>

                                                    <Collapse in={expandedCustomer === (customer.customer_id || customer.id)} animateOpacity>
                                                        <Box mt={6} pt={6} borderTop="1px solid" borderColor="gray.200">
                                                            {renderCustomerBiasReport(customer.customer_id || customer.id)}
                                                        </Box>
                                                    </Collapse>
                                                </CardBody>
                                            </Card>
                                        ))}
                                    </VStack>
                                )}

                                {/* Pagination */}
                                {!loading && customers.length > 0 && (
                                    <Card bg="white" borderRadius="lg" boxShadow="md">
                                        <CardBody>
                                            <Flex justify="space-between" align="center">
                                                <Text fontSize="sm" color="gray.600">
                                                    Showing {customers.length} of {pagination.total_count} customers
                                                </Text>
                                                <HStack>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => fetchCustomers(pagination.page - 1, searchTerm)}
                                                        isDisabled={pagination.page === 1}
                                                        colorScheme="blue"
                                                        variant="outline"
                                                    >
                                                        Previous
                                                    </Button>
                                                    <Text fontSize="sm" color="gray.600">Page {pagination.page}</Text>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => fetchCustomers(pagination.page + 1, searchTerm)}
                                                        isDisabled={customers.length < pagination.page_size}
                                                        colorScheme="blue"
                                                        variant="outline"
                                                    >
                                                        Next
                                                    </Button>
                                                </HStack>
                                            </Flex>
                                        </CardBody>
                                    </Card>
                                )}
                            </>
                        )}

                        {/* Premium content - RM view */}
                        {['solo', 'pro', 'business'].includes(user.subscription) && user.role === 'rm' && (
                            <>
                                <Box>
                                    <Heading size="lg" color="brand.navy" mb={2}>My Reports & Exports</Heading>
                                    <Text color="gray.600" fontSize="sm">
                                        Download reports for your customers
                                    </Text>
                                </Box>

                                <Card bg="white" borderRadius="lg" boxShadow="md">
                                    <CardBody>
                                        <Heading size="md" mb={4} color="brand.navy">Export My Data</Heading>
                                        <Button
                                            leftIcon={<DownloadIcon />}
                                            colorScheme="blue"
                                            bg="brand.navy"
                                            onClick={exportMyCustomersCSV}
                                            isLoading={exportingCSV}
                                            _hover={{ bg: 'brand.navy2' }}
                                        >
                                            Export My Customers
                                        </Button>
                                    </CardBody>
                                </Card>
                            </>
                        )}
                    </VStack>
                </Container>
            </Box>
            <Footer />
        </Box>
    );
}

export default ReportsExports;
